import React, { useCallback } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import isBetween from "dayjs/plugin/isBetween"
import { useTranslation } from "react-i18next"
import { DayPicker } from "react-day-picker"
import { Typography, Flex } from "antd"
import "react-day-picker/style.css"
import styles from "./ExperienceBooking.module.css"

dayjs.extend(isBetween)

const { Text } = Typography

const DateSelector = ({ value, onChange, activeTab, error, trip }) => {
  const [t] = useTranslation()

  const disabledDate = useCallback(
    (current) => {
      const today = dayjs().startOf("day")
      const date = dayjs(current)
      const scheduledDeletionDate = trip?.guide?.scheduled_deletion_date

      if (!current || !trip) return true

      const weekdayMap = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6
      }

      const blockedWeekdays = trip?.unavailable_weekdays?.map((day) => weekdayMap[day.toLowerCase()]) || []

      const isSoldOut = trip?.sold_out_dates?.some((dateStr) => date.isSame(dayjs(dateStr), "day"))
      const isUnavailable = trip?.unavailable_dates?.some((dateStr) => date.isSame(dayjs(dateStr), "day"))

      const hasDateRanges = Array.isArray(trip?.available_date_ranges) && trip.available_date_ranges.length > 0

      let isInAvailableRange = true
      if (hasDateRanges) {
        isInAvailableRange = trip.available_date_ranges.some((range) => {
          const start = dayjs(range.start).startOf("day")
          const end = dayjs(range.end).endOf("day")
          return date.isBetween(start, end, null, "[]")
        })
      }

      return (
        date.isBefore(today) ||
        date.isAfter(scheduledDeletionDate) ||
        blockedWeekdays.includes(date.day()) ||
        isUnavailable ||
        isSoldOut ||
        (hasDateRanges && !isInAvailableRange)
      )
    },
    [trip]
  )

  if (activeTab === 2 || activeTab === 3) {
    return (
      <Flex className={styles.inputContainer}>
        <span className={styles.inputLabel}>Date</span>
        <span className={styles.selectedInputLink} style={{ color: "var(--color-text-secondary-dark)" }}>
          {value ? dayjs(value).format("MM/DD/YY") : "—"}
        </span>
      </Flex>
    )
  }

  return (
    <div className={styles.guestsWrapper}>
      <div className={`${styles.inlineCalendar} ${error ? styles.errorBorder : ""}`}>
        <div className={styles.calendarLabel}>Choose Date</div>
        <DayPicker
          mode="single"
          captionLayout="label"
          selected={value ? value.toDate() : undefined}
          onSelect={(date) => {
            if (date) onChange(dayjs(date))
          }}
          disabled={disabledDate}
          startMonth={dayjs().startOf("month").toDate()}
          showOutsideDays
        />
      </div>
      {error && <Text style={{ color: "var(--color-error)" }}>{t("webIntegration.embedded_validation.date")}</Text>}
    </div>
  )
}

DateSelector.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  activeTab: PropTypes.number,
  error: PropTypes.bool,
  trip: PropTypes.object
}

export default React.memo(DateSelector)
